import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { ScrollView } from "react-native";
import { useFragment } from "react-relay";

import { Button } from "@whoosh/design-system/src/components/controls";
import { CloseIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import { PanelHeader } from "@whoosh/design-system/src/components/layout/PanelHeader";
import { VStack } from "@whoosh/design-system/src/components/primitive";

import { anyBookingRatesAtom } from "../../atoms/selectors/anyBookingRates/anyBookingRates";
import { EditableRoundsStep_Booking$key } from "./__generated__/EditableRoundsStep_Booking.graphql";
import { ActionButtons } from "./components/ActionButtons/ActionButtons";
import { BookingDurationCourt } from "./components/BookingDurationCourt/BookingDurationCourt";
import { BookingRatesControl } from "./components/BookingRates/BookingRatesControl";
import { EditableRoundsList } from "./components/EditableRoundsList/EditableRoundsList";
import { RequestTimeSelectionControl } from "./components/RequestTimeSelectionControl/RequestTimeSelectionControl";

type Props = {
  onClose: () => void;
  onSaveSuccess: () => void;
  bookingRef: EditableRoundsStep_Booking$key;
  currentMembershipTypeId: string;
  currentSessionUserClientId: string;
};
export const EditableRoundsStep = ({
  onClose,
  onSaveSuccess,
  bookingRef,
  currentMembershipTypeId,
  currentSessionUserClientId,
}: Props) => {
  const booking = useFragment(EditableRoundsStepFragmentString, bookingRef);

  const isDurationBasedFacility =
    (booking?.startSlot?.agenda?.facility.type &&
      booking?.startSlot?.agenda?.facility.type !== "GOLF_COURSE") ??
    false;

  const shouldShowDurationControl =
    isDurationBasedFacility && booking.startSlot;

  const shouldShowRequestTimeControl = booking?.request?.id;

  const groupSize = booking?.rounds?.count;

  const anyBookingRates = useAtomValue(anyBookingRatesAtom);

  const isLessonBooking = booking.__typename === "LessonBooking";

  const isQueueSlot = booking?.startSlot?.__typename === "QueueSlot";

  const shouldShowBookingRatesControl =
    anyBookingRates &&
    booking.durationMins &&
    groupSize &&
    groupSize > 0 &&
    !isLessonBooking;

  const isPrivate =
    booking?.startSlot?.agenda?.facility?.club?.supportsPrivate ?? false;

  return (
    <VStack
      flex={1}
      backgroundColor="surface-secondary"
      testID="editable-rounds-step"
    >
      <PanelHeader
        paddingVariant="medium"
        header={isQueueSlot ? t`Edit Request` : t`Edit Booking`}
        outerLeftComponent={
          <Button
            accessibilityLabel={t`Close`}
            testID="modal-close-btn"
            variant="secondary"
            size="small"
            onPress={onClose}
            leftIcon={<CloseIcon />}
          />
        }
      />
      <ScrollView style={{ flex: 1, paddingBottom: 16 }}>
        {shouldShowRequestTimeControl ? (
          <RequestTimeSelectionControl
            timeSlotsFragmentRef={booking?.startSlot?.agenda ?? null}
            isLessonBooking={isLessonBooking}
            instructorId={
              booking.__typename === "LessonBooking"
                ? booking.instructors?.edges?.[0]?.node?.id ?? null
                : null
            }
            lessonTypeId={
              booking.__typename === "LessonBooking"
                ? booking.lessonType?.id ?? null
                : null
            }
            durationMins={booking.durationMins}
            facilityId={booking.startSlot?.agenda?.facility.id ?? null}
            date={booking.startSlot?.agenda?.date ?? null}
          />
        ) : null}
        {shouldShowDurationControl ? (
          <BookingDurationCourt
            currentSessionUserClientId={currentSessionUserClientId}
            slotRef={booking.startSlot}
            durationMins={booking.durationMins ?? 15}
            isLessonBooking={isLessonBooking}
          />
        ) : null}
        {shouldShowBookingRatesControl ? (
          <BookingRatesControl
            bookingId={booking.id}
            currentMembershipTypeId={currentMembershipTypeId}
            durationMins={booking.durationMins}
            groupSize={groupSize}
          />
        ) : null}
        <EditableRoundsList
          bookingRef={booking}
          currentMembershipTypeId={currentMembershipTypeId}
          isPrivate={isPrivate}
        />
      </ScrollView>
      <ActionButtons
        onClose={onClose}
        onSaveSuccess={onSaveSuccess}
        isDurationBasedFacility={isDurationBasedFacility}
        isLessonBooking={isLessonBooking}
      />
    </VStack>
  );
};

const EditableRoundsStepFragmentString = graphql`
  fragment EditableRoundsStep_Booking on Booking {
    __typename
    ...EditableRoundsList_Booking
    ...useGetBookingRestrictionStatusesFragment
    ... on StandardBooking {
      status
      durationMins
      request {
        id
      }
    }
    ... on LessonBooking {
      durationMins
      request {
        id
      }
      lessonType {
        id
      }
      instructors {
        edges {
          node {
            id
          }
        }
      }
    }
    rounds {
      count
    }
    id
    startSlot {
      __typename
      id
      ...BookingDurationCourtFragment
      ...BookingDurationCourtInnerFragment

      agenda {
        id
        date
        facility {
          id
          type
          club {
            supportsPrivate
          }
        }
        ...RequestTimeSelectionControl_TimeSlots
      }
    }
  }
`;
